import { Component, inject, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { PaginatorModule } from 'primeng/paginator';
import { SeoService } from '../../core/services/seo.service';
import { GewerkService } from '../../core/services/gewerk.service';
import { HandwerkerService } from '../../core/services/handwerker.service';
import { PlacesService, PlaceSuggestion, PlaceDetails } from '../../core/services/places.service';
import { GewerkDto } from '../../core/models/gewerk.model';
import { HandwerkerSucheDto, PagedResult } from '../../core/models/handwerker-suche.model';
import { HandwerkerKarteComponent } from '../../shared/components/handwerker-karte/handwerker-karte.component';

@Component({
  selector: 'app-suche',
  standalone: true,
  imports: [
    FormsModule,
    PaginatorModule,
    HandwerkerKarteComponent,
  ],
  templateUrl: './suche.component.html',
  styleUrl: './suche.component.scss',
})
export class SucheComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly gewerkService = inject(GewerkService);
  private readonly handwerkerService = inject(HandwerkerService);
  private readonly placesService = inject(PlacesService);
  private readonly seo = inject(SeoService);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly destroy$ = new Subject<void>();
  private readonly ortSearch$ = new Subject<string>();

  gewerke: GewerkDto[] = [];

  // Search form state — null = "Alle"
  selectedGewerk: string | null = null;
  ortInput = '';
  radiusKm = 25;

  // Gewerk autocomplete (text input)
  gewerkInput = '';
  filteredGewerke: GewerkDto[] = [];
  gewerkDropdownOpen = false;

  // Ort autocomplete (Mapbox place mode)
  ortSuggestions: PlaceSuggestion[] = [];
  ortDropdownOpen = false;
  /** Vom Nutzer gewählter Ort inkl. Koordinaten — Quelle der Wahrheit für lat/lng. */
  private selectedOrt: PlaceDetails | null = null;
  /** Mapbox-Session-Token für die laufende Eingabe. */
  private ortSessionToken = '';

  // Results
  result: PagedResult<HandwerkerSucheDto> | null = null;
  loading = false;
  geocodeError = false;

  readonly skeletonItems = Array(8);
  view: 'list' | 'map' = 'list';

  ngOnInit(): void {
    this.gewerkService.getAll().subscribe((g) => {
      this.gewerke = g;
      this.filteredGewerke = g;
      this.syncGewerkInputFromSelection();
    });

    if (isPlatformBrowser(this.platformId)) {
      this.ortSearch$.pipe(
        debounceTime(300),
        switchMap((query) => {
          if (!query.trim() || query.length < 2) return of<PlaceSuggestion[]>([]);
          if (!this.ortSessionToken) this.ortSessionToken = this.placesService.newSessionToken();
          return this.placesService.suggest(query, 'place', this.ortSessionToken);
        }),
        takeUntil(this.destroy$),
      ).subscribe((suggestions) => {
        this.ortSuggestions = suggestions;
        this.ortDropdownOpen = suggestions.length > 0;
      });
    }

    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((params) => {
        const gewerk = params.get('gewerk');
        const ort = params.get('ort') ?? '';
        const latStr = params.get('lat');
        const lngStr = params.get('lng');
        const seite = parseInt(params.get('seite') ?? '1', 10);

        const lat = latStr ? parseFloat(latStr) : NaN;
        const lng = lngStr ? parseFloat(lngStr) : NaN;
        const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);

        this.ortInput = ort;
        this.selectedGewerk = gewerk;
        this.syncGewerkInputFromSelection();
        this.updateSeo(gewerk);
        this.loadResults(
          gewerk,
          ort,
          hasCoords ? lat : null,
          hasCoords ? lng : null,
          seite,
        );
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Submit triggered from the hero search bar. */
  onSuche(): void {
    // Try to resolve a free-text Gewerk to an id if user typed a label.
    if (!this.selectedGewerk && this.gewerkInput.trim()) {
      const text = this.gewerkInput.trim().toLowerCase();
      const match = this.gewerke.find((g) => g.label.toLowerCase() === text);
      if (match) this.selectedGewerk = match.id;
    }

    const queryParams: Record<string, string | number> = {};
    if (this.selectedGewerk) queryParams['gewerk'] = this.selectedGewerk;
    this.appendOrtParams(queryParams);
    this.router.navigate([], { queryParams, replaceUrl: true });
  }

  // --- Chip filter row ---

  onChipAll(): void {
    const queryParams: Record<string, string | number> = {};
    this.appendOrtParams(queryParams);
    this.router.navigate([], { queryParams, replaceUrl: true });
  }

  onChipSelect(g: GewerkDto): void {
    const queryParams: Record<string, string | number> = { gewerk: g.id };
    this.appendOrtParams(queryParams);
    this.router.navigate([], { queryParams, replaceUrl: true });
  }

  getGewerkLabel(id: string): string {
    return this.gewerke.find((g) => g.id === id)?.label ?? id;
  }

  // --- Gewerk autocomplete ---

  onGewerkInput(): void {
    const val = this.gewerkInput.toLowerCase().trim();
    this.filteredGewerke = val
      ? this.gewerke.filter((g) => g.label.toLowerCase().includes(val))
      : this.gewerke;
    this.gewerkDropdownOpen = true;
    this.selectedGewerk = null;
  }

  onGewerkFocus(): void {
    if (!this.gewerkInput) this.filteredGewerke = this.gewerke;
    this.gewerkDropdownOpen = true;
  }

  onGewerkBlur(): void {
    setTimeout(() => { this.gewerkDropdownOpen = false; }, 150);
  }

  selectGewerkOption(g: GewerkDto | null): void {
    this.selectedGewerk = g?.id ?? null;
    this.gewerkInput = g?.label ?? '';
    this.gewerkDropdownOpen = false;
  }

  // --- Ort autocomplete ---

  onOrtInput(): void {
    // Sobald der Nutzer weiter tippt, ist die alte Auswahl ungültig.
    this.selectedOrt = null;
    this.ortSuggestions = [];
    this.ortDropdownOpen = false;
    this.ortSearch$.next(this.ortInput);
  }

  onOrtFocus(): void {
    if (this.ortSuggestions.length > 0) this.ortDropdownOpen = true;
  }

  onOrtBlur(): void {
    setTimeout(() => { this.ortDropdownOpen = false; }, 150);
  }

  selectOrt(s: PlaceSuggestion): void {
    this.ortInput = PlacesService.shortLabel(s);
    this.ortDropdownOpen = false;
    this.ortSuggestions = [];

    this.placesService.retrieve(s.mapboxId, this.ortSessionToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe((details) => {
        this.ortSessionToken = '';
        if (details) this.selectedOrt = details;
      });
  }

  onPageChange(event: { first?: number; rows?: number }): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? 20;
    const seite = Math.floor(first / rows) + 1;
    this.router.navigate([], {
      queryParams: { seite },
      queryParamsHandling: 'merge',
    });
  }

  // --- Internals ---

  /**
   * Hängt den Ort + (falls vorhanden) Koordinaten an die Query-Params an.
   * Vorrang hat selectedOrt — nur wenn der nicht gesetzt ist, wird der
   * freie Text genutzt und /suche muss selbst forward-geocoden.
   */
  private appendOrtParams(params: Record<string, string | number>): void {
    if (this.selectedOrt) {
      params['ort'] = PlacesService.shortLabel(this.selectedOrt);
      params['lat'] = this.selectedOrt.lat;
      params['lng'] = this.selectedOrt.lng;
    } else if (this.ortInput.trim()) {
      params['ort'] = this.ortInput.trim();
    }
  }

  private syncGewerkInputFromSelection(): void {
    this.gewerkInput = this.selectedGewerk
      ? this.getGewerkLabel(this.selectedGewerk)
      : '';
  }

  private updateSeo(gewerkId: string | null): void {
    if (gewerkId) {
      const label = this.gewerke.find((g) => g.id === gewerkId)?.label ?? gewerkId;
      this.seo.apply({
        title: `${label} in Ihrer Nähe – Craftly`,
        description: `Geprüfte ${label} in Ihrer Nähe finden und direkt online buchen. Festpreis vor Auftrag, Treuhand-Zahlung.`,
        path: `/suche?gewerk=${gewerkId}`,
      });
    } else {
      this.seo.apply({
        title: 'Handwerker finden – Craftly',
        description: 'Finden Sie geprüfte Handwerker in Ihrer Nähe – alle Gewerke, ein Ort.',
        path: '/suche',
      });
    }
  }

  private loadResults(
    gewerk: string | null,
    ort: string,
    lat: number | null,
    lng: number | null,
    seite: number,
  ): void {
    this.loading = true;
    this.geocodeError = false;

    // Coords aus URL ⇒ kein zusätzlicher Geocoding-Roundtrip.
    if (lat != null && lng != null) {
      this.fetchResults(gewerk, lat, lng, seite);
      return;
    }
    if (!ort.trim()) {
      this.fetchResults(gewerk, undefined, undefined, seite);
      return;
    }

    // Direkter URL-Load mit nur ?ort=... — Single-Shot Forward-Geocoding.
    this.placesService
      .forwardGeocode(ort, 'place')
      .pipe(takeUntil(this.destroy$))
      .subscribe((pos) => {
        if (pos) {
          // Auch merken, damit Chip-Klicks die Koordinaten weiterreichen können.
          this.selectedOrt = pos;
          this.fetchResults(gewerk, pos.lat, pos.lng, seite);
        } else {
          this.geocodeError = true;
          this.fetchResults(gewerk, undefined, undefined, seite);
        }
      });
  }

  private fetchResults(
    gewerk: string | null,
    lat: number | undefined,
    lng: number | undefined,
    seite: number,
  ): void {
    this.handwerkerService
      .suche({ gewerk: gewerk ?? undefined, lat, lng, seite, proSeite: 20 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (r) => {
          this.result = r;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }
}
